import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ShieldCheck, UserPlus, X, Mail, Copy, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminApprovals = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ name: '', email: '', phone: '', role: 'employee', department: '', notes: '' });
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, user: null, note: '' });

  useEffect(() => { fetchPending(); }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/pending`, { withCredentials: true });
      setPending(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pending users');
    }
    setLoading(false);
  };

  const approve = async (u) => {
    try {
      await axios.post(`${API}/admin/users/${u.id}/approve`, { note: '' }, { withCredentials: true });
      toast.success(`Approved ${u.name}`);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
  };

  const submitReject = async () => {
    try {
      await axios.post(`${API}/admin/users/${rejectDialog.user.id}/reject`, { note: rejectDialog.note }, { withCredentials: true });
      toast.success('User rejected');
      setRejectDialog({ open: false, user: null, note: '' });
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    }
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviteSending(true);
    setInviteResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/users/invite`, invite, { withCredentials: true });
      setInviteResult(data);
      toast.success('Invitation prepared');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    }
    setInviteSending(false);
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link).then(
      () => toast.success('Invite link copied'),
      () => toast.error('Could not copy')
    );
  };

  const closeInviteDialog = () => {
    setShowInvite(false);
    setInvite({ name: '', email: '', phone: '', role: 'employee', department: '', notes: '' });
    setInviteResult(null);
  };

  return (
    <div data-testid="admin-approvals-page" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Pending Approvals & Invites</h2>
          <p className="text-[#A3A3A3]">VIPs and Employees need admin approval before they can sign in.</p>
        </div>
        <Dialog open={showInvite} onOpenChange={(o) => { if (!o) closeInviteDialog(); else setShowInvite(true); }}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-btn" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
              <UserPlus className="w-4 h-4 mr-2" /> Invite Employee / VIP
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-md">
            <DialogHeader><DialogTitle>Invite a new user</DialogTitle></DialogHeader>
            {!inviteResult ? (
              <form onSubmit={sendInvite} className="space-y-3">
                <div>
                  <Label className="text-white">Role</Label>
                  <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                    <SelectTrigger data-testid="invite-role" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      <SelectItem value="employee" className="text-white">Employee</SelectItem>
                      <SelectItem value="vip" className="text-white">VIP Referral Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Full Name</Label>
                  <Input data-testid="invite-name" required value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div>
                  <Label className="text-white">Email</Label>
                  <Input data-testid="invite-email" type="email" required value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <div>
                  <Label className="text-white">Phone <span className="text-[#525252] text-xs">(optional)</span></Label>
                  <Input data-testid="invite-phone" type="tel" value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                {invite.role === 'employee' && (
                  <div>
                    <Label className="text-white">Department</Label>
                    <Select value={invite.department || ''} onValueChange={(v) => setInvite({ ...invite, department: v })}>
                      <SelectTrigger data-testid="invite-department" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue placeholder="Pick a department" /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-[#262626]">
                        <SelectItem value="service" className="text-white">Service</SelectItem>
                        <SelectItem value="new_construction" className="text-white">New Construction</SelectItem>
                        <SelectItem value="office" className="text-white">Office</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="text-white">Notes <span className="text-[#525252] text-xs">(optional)</span></Label>
                  <Textarea data-testid="invite-notes" rows={2} value={invite.notes} onChange={(e) => setInvite({ ...invite, notes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
                </div>
                <Button data-testid="invite-submit" type="submit" disabled={inviteSending} className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
                  <Mail className="w-4 h-4 mr-2" /> {inviteSending ? 'Sending…' : 'Send Invite'}
                </Button>
              </form>
            ) : (
              <div className="space-y-3" data-testid="invite-result">
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-md p-3">
                  <p className="text-white text-sm font-medium">Invite created.</p>
                  <p className="text-[#A3A3A3] text-xs mt-1">
                    Delivery mode: <span className="text-[#D4AF37] uppercase">{inviteResult.delivery_mode}</span>
                    {inviteResult.delivery_mode === 'preview' && (
                      <> — email is not wired yet. Share this link directly:</>
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-white">Invite Link</Label>
                  <div className="flex gap-2 mt-1">
                    <Input data-testid="invite-link" readOnly value={inviteResult.invite_link} className="bg-[#0A0A0A] border-[#262626] text-white font-mono text-xs" />
                    <Button type="button" data-testid="copy-invite-link" onClick={() => copyLink(inviteResult.invite_link)} className="bg-[#262626] text-white hover:bg-[#404040]"><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>
                <p className="text-[#A3A3A3] text-xs">Expires {inviteResult.expires_at ? new Date(inviteResult.expires_at).toLocaleDateString() : 'soon'}.</p>
                <Button onClick={closeInviteDialog} className="w-full bg-[#262626] text-white hover:bg-[#404040]">Close</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#141414] border border-[#262626] rounded-md">
        <div className="p-4 border-b border-[#262626] flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-white font-medium">Awaiting Approval</span>
          <Badge data-testid="pending-count" className="ml-2 bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30">{pending.length}</Badge>
        </div>
        {loading ? (
          <div className="p-8 text-[#A3A3A3] text-center">Loading…</div>
        ) : pending.length === 0 ? (
          <div data-testid="approvals-empty" className="p-8 text-center text-[#A3A3A3]">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-[#D4AF37]" />
            No pending approvals right now.
          </div>
        ) : (
          <ul className="divide-y divide-[#262626]">
            {pending.map((u) => (
              <li key={u.id} data-testid={`pending-row-${u.id}`} className="p-4 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-white font-medium">{u.name} <span className="text-[#A3A3A3] text-sm">· {u.role.toUpperCase()}</span></p>
                  <p className="text-[#A3A3A3] text-sm">{u.email} {u.phone ? `· ${u.phone}` : ''}</p>
                  {u.department && <p className="text-[#525252] text-xs">Dept: {u.department}</p>}
                  <p className="text-[#525252] text-xs">Registered {u.created_at ? new Date(u.created_at).toLocaleString() : ''}</p>
                  {!u.email_verified && (
                    <p className="text-amber-500 text-xs mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Email not verified yet</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`approve-${u.id}`}
                    onClick={() => approve(u)}
                    className="bg-green-600 text-white hover:bg-green-700"
                  >Approve</Button>
                  <Button
                    data-testid={`reject-${u.id}`}
                    onClick={() => setRejectDialog({ open: true, user: u, note: '' })}
                    variant="outline"
                    className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white"
                  ><X className="w-4 h-4 mr-1" /> Reject</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Reject dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(o) => !o && setRejectDialog({ open: false, user: null, note: '' })}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader><DialogTitle>Reject {rejectDialog.user?.name}?</DialogTitle></DialogHeader>
          <p className="text-[#A3A3A3] text-sm">This will deactivate the account. They won't be able to sign in.</p>
          <div>
            <Label className="text-white">Reason (optional)</Label>
            <Textarea data-testid="reject-note" rows={3} value={rejectDialog.note} onChange={(e) => setRejectDialog({ ...rejectDialog, note: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="border-[#262626] text-white" onClick={() => setRejectDialog({ open: false, user: null, note: '' })}>Cancel</Button>
            <Button data-testid="confirm-reject" onClick={submitReject} className="bg-red-600 text-white hover:bg-red-700">Confirm Reject</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminApprovals;
